import { z } from 'zod';

/**
 * W4b PEOC Arrivals (coordination-tab council ruling A.6, migration 0128) — the
 * normalized ARRIVAL row the read-model (GET /api/peoc-arrivals) returns.
 *
 * The one "PEOC Arrivals" surface unions THREE first-notice legs:
 *   (a) declarations — operator_emergency_declarations WHERE peoc_notified_at IS NOT NULL
 *       (authority_kind municipal | first_nation; arrived_at = peoc_notified_at);
 *   (b) the FN PEOC/ISC dual-notify — NOT a separate arrival: a BADGE on the (a) row
 *       (isc_notified_at present → "ISC also notified"; "one act, two legs", mig 0126);
 *   (c) Area-Command establishments — incidents WHERE is_area_command = true
 *       (arrived_at = opened_at).
 *
 * An arrival is ACKNOWLEDGED (records receipt). The PEOC RECEIVES + coordinates support
 * (App A #46 Liaison) — it NEVER commands / approves / adjudicates (PERP §6.8.1.2). The
 * ack is NOT App A #9 Check-in. The operator-facing copy (labels / caption / chips) lives
 * in apps/web/src/peocArrivalsVocabulary.ts (pinned by its lock test); this module is the
 * DATA shape only.
 */

/** An arrival's SOURCE leg. Kept in lockstep with valid_peoc_arrival_source_types (0128). */
export const PEOC_ARRIVAL_SOURCE_TYPES = ['declaration', 'area_command'] as const;
export const PeocArrivalSourceTypeSchema = z.enum(PEOC_ARRIVAL_SOURCE_TYPES);
export type PeocArrivalSourceType = z.infer<typeof PeocArrivalSourceTypeSchema>;

export interface PeocArrival {
  /** Which first-notice leg produced this arrival. */
  source_type: PeocArrivalSourceType;
  /** The polymorphic source id — a declaration.id (leg a) or an incidents.id (leg c). */
  source_id: string;
  /** 'municipal' | 'first_nation' for a declaration; null for an Area-Command establishment. */
  authority_kind: 'municipal' | 'first_nation' | null;
  /** The declaring jurisdiction name (leg a) or the Area Command name (leg c). */
  title: string;
  /** A human reference — the DECL-YYYY-NNNNN number (leg a) or the incident number (leg c). */
  reference: string | null;
  /** The COP hazard enum, when the source carries one (never a raw external value). */
  hazard_type: string | null;
  /** When the arrival reached the PEOC (peoc_notified_at | opened_at), ISO-8601 UTC. */
  arrived_at: string;
  /** FN dual-notify badge (leg b): set when ISC was co-notified. ISO-8601 UTC | null. */
  isc_notified_at: string | null;
  /** The linked incident (leg a context link, or the Area Command's own id on leg c). */
  incident_id: string | null;
  /** Receipt stamp — when a PEOC duty officer acknowledged receipt. ISO-8601 UTC | null. */
  acknowledged_at: string | null;
  /** The receiving PEOC duty officer (records receipt; never an approver). */
  acknowledged_by: string | null;
}
